import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, FormControlLabel, Stack, Box, IconButton, Typography, } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { navStore, useStore } from '@mcn-platform/models';
const getMeetingUrl = (pathname, meetingId) => {
    const segments = pathname.split('/').filter(Boolean);
    if (segments[0] === 'portal') {
        const basePath = `/${segments.slice(0, 3).join('/')}`;
        return `${basePath}/meeting/${meetingId}`;
    }
    return `/meeting/${meetingId}`;
};
const MeetNowWidget = ({ settings, }) => {
    const getSetting = (name) => {
        if (settings[name] !== undefined) {
            return settings[name];
        }
        return MeetNowWidget.defaultProps.settings[name];
    };
    const enableInviteFeature = getSetting('enableInviteFeature');
    const buttonLabel = getSetting('buttonLabel');
    const openInNewTab = getSetting('openInNewTab');
    const [open, setOpen] = useState(false);
    const [meetingName, setMeetingName] = useState('');
    const [subject, setSubject] = useState('You Have Been Invited to a Meeting');
    const [message, setMessage] = useState('');
    const [emailAddresses, setEmailAddresses] = useState(['']);
    const navigate = useNavigate();
    const store = useStore();
    const { createMeeting, startMeeting, registerForMeeting, 
    // joinMeeting,
    sendMeetingInvites, } = store;
    const handleOpen = () => {
        setMeetingName(`Meet Now ${new Date().toLocaleString()}`);
        setOpen(true);
    };
    const handleClose = () => {
        setOpen(false);
        setMeetingName('');
    };
    const handleEmailAddressChange = (index, value) => {
        const newEmailAddresses = [...emailAddresses];
        newEmailAddresses[index] = value;
        setEmailAddresses(newEmailAddresses);
    };
    const handleAddEmailAddress = () => {
        setEmailAddresses([...emailAddresses, '']);
    };
    const handleRemoveEmailAddress = (index) => {
        const newEmailAddresses = [...emailAddresses];
        newEmailAddresses.splice(index, 1);
        setEmailAddresses(newEmailAddresses);
    };
    const handleMeetNow = async () => {
        try {
            navStore.showBlockInput();
            const newMeeting = await createMeeting(meetingName);
            await startMeeting(newMeeting.id);
            await registerForMeeting(newMeeting.id);
            if (enableInviteFeature && emailAddresses.length > 0) {
                const validEmailAddresses = emailAddresses
                    .map((email) => email.trim())
                    .filter((email) => email !== '');
                if (validEmailAddresses.length > 0) {
                    await sendMeetingInvites(newMeeting.id, subject, message, validEmailAddresses);
                }
            }
            if (openInNewTab) {
                const meetingUrl = `${window.location.origin}${getMeetingUrl(window.location.pathname, newMeeting.id)}`;
                window.open(meetingUrl, '_blank');
            }
            else {
                navigate(`./meeting/${newMeeting.key || newMeeting.id}`);
            }
            handleClose();
        }
        catch (error) {
            console.error("Error on meet now:", error);
            handleClose();
        }
        finally {
            navStore.clearBlockInput();
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "contained", color: "primary", onClick: handleOpen, children: buttonLabel }), _jsxs(Dialog, { open: open, onClose: handleClose, children: [_jsx(DialogTitle, { children: "Start a Meeting" }), _jsx(DialogContent, { children: _jsx(Box, { sx: { width: '400px' }, children: _jsxs(Stack, { direction: "column", spacing: 2, sx: { width: '100%', mt: 2 }, children: [_jsx(FormControlLabel, { label: "Meeting Name", labelPlacement: "top", control: (_jsx(TextField, { value: meetingName, onChange: (e) => setMeetingName(e.target.value), placeholder: "Enter meeting name", fullWidth: true, required: true })) }), enableInviteFeature && (_jsxs(_Fragment, { children: [_jsx(FormControlLabel, { label: "Subject", labelPlacement: "top", control: (_jsx(TextField, { value: subject, onChange: (e) => setSubject(e.target.value), placeholder: "Enter subject", fullWidth: true, required: true })) }), _jsx(FormControlLabel, { label: "Message", labelPlacement: "top", control: (_jsx(TextField, { value: message, onChange: (e) => setMessage(e.target.value), placeholder: "Enter message", fullWidth: true, multiline: true, rows: 4 })) }), _jsxs(Box, { children: [_jsx(Typography, { variant: "subtitle1", children: "Email Addresses" }), emailAddresses.map((email, index) => (_jsxs(Box, { display: "flex", alignItems: "center", mt: 1, children: [_jsx(TextField, { value: email, onChange: (e) => handleEmailAddressChange(index, e.target.value), placeholder: "Enter email address", fullWidth: true }), _jsx(IconButton, { onClick: () => handleRemoveEmailAddress(index), disabled: emailAddresses.length === 1, children: _jsx(RemoveIcon, {}) })] }, index))), _jsx(Button, { variant: "text", onClick: handleAddEmailAddress, startIcon: _jsx(AddIcon, {}), children: "Add Email Address" })] })] }))] }) }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: handleClose, color: "secondary", children: "Cancel" }), _jsx(Button, { onClick: handleMeetNow, variant: "contained", color: "primary", children: "Start Meeting" })] })] })] }));
};
MeetNowWidget.defaultProps = {
    settings: {
        buttonLabel: 'Meet Now',
        enableInviteFeature: false,
        openInNewTab: false,
    },
    editData: undefined,
};
MeetNowWidget.meta = {
    group: 'design',
    name: 'Meet Now Widget',
    description: 'Opens a dialog to create a new meeting and start it instantly.',
    props: [
        {
            key: 'settings.buttonLabel',
            name: 'Button Label',
            type: 'text',
            description: 'Label for the Meet Now button.',
        },
        {
            key: 'settings.enableInviteFeature',
            name: 'Enable Invite Feature',
            type: 'boolean',
            description: 'Enable or disable the invite feature in the Meet Now dialog.',
        },
        {
            key: 'settings.openInNewTab',
            name: 'Open In New Tab',
            type: 'boolean',
            description: 'If true, the meeting will open in a new tab.',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'MeetNowWidget',
        props: {
            settings: {
                buttonLabel: 'Meet Now',
                enableInviteFeature: false,
                openInNewTab: false,
            },
        },
    },
};
export default observer(MeetNowWidget);
//# sourceMappingURL=MeetNowWidget.js.map